import { useState } from 'react'

import dayjs from 'dayjs'

import { useAnalyticsRecommendationPerformanceGetApi } from '@/common/api/modules/analytics'
import { useSiteGetApi } from '@/common/api/modules/site'
import {
  useDeliveryStatsGetApi,
  usePlacementStatsGetApi,
  useSiteDashboardGetApi
} from '@/common/api/modules/site'
import { PlacementStat, WidgetDeliveryStat } from '@/common/api/params/site'
import { currencyText } from '@/common/app/currency'
import { widgetDisplayLogicName } from '@/common/app/modules/recommend/widget'
import { useSiteId } from '@/common/app/site'
import { dateStr } from '@/common/form/date'
import {
  ConversionAttribution,
  DEFAULT_CONVERSION_ATTRIBUTION,
  pickConversion
} from '@/common/model/recommend/conversion'
import { formatPercent } from '@/common/util/number'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import SortButton from '@/components/view/atoms/button/SortButton'
import BarAndLineChart from '@/components/view/atoms/chart/BarAndLineChart'
import BarChart from '@/components/view/atoms/chart/BarChart'
import ChartTitle from '@/components/view/atoms/chart/ChartTitle'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import SimplePager from '@/components/view/atoms/pager/SimplePager'
import TextPager from '@/components/view/atoms/pager/TextPager'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import ChartWrapper from '@/components/view/layouts/chart/ChartWrapper'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import DisclosureSortDateBetweenButton from '@/components/view/molecules/disclosure/DisclosureSortDateBetweenButton'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import MainInnerLoader from '@/components/container/MainInnerLoader'
import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import ConversionAttributionToggle from '@/components/container/modules/recommend/ConversionAttributionToggle'
import {
  ActionTransition,
  RecommendResult
} from '@/components/container/page/site/dashboard/SiteDashboardPage'

const PERIOD_OPTIONS = [
  { value: 7, label: '7日間' },
  { value: 14, label: '14日間' },
  { value: 30, label: '30日間' },
  { value: 90, label: '90日間' }
]

const SOURCE_LABELS: Record<string, string> = {
  BB: 'サイト',
  OB: 'サイト',
  personal: 'サイト',
  similar: 'サイト',
  mail: 'メール'
}

// 折れ線グラフへ渡す率。BarAndLineChart は受け取った数値へそのまま % を付けて描くため、
// 比率ではなく「％の数値」を渡す。小数を切り捨てると 1%未満の CVR が全て 0% に潰れるので、
// テーブル側の formatPercent と同じ小数2桁で丸める
const percentPoint = (numerator: number, denominator: number) =>
  denominator > 0 ? Number(((numerator / denominator) * 100).toFixed(2)) : 0

const formatDwell = (ms: number | null | undefined) => {
  if (ms == null) return '-'
  if (ms < 60000) return Math.round(ms / 1000) + '秒'
  const min = Math.floor(ms / 60000)
  const sec = Math.round((ms % 60000) / 1000)
  return min + '分' + (sec > 0 ? sec + '秒' : '')
}

interface PeriodTabsProps {
  days: number
  period: { from: Date | undefined; to: Date | undefined } | null
  onChange(days: number, period: { from: Date | undefined; to: Date | undefined } | null): void
}

const PeriodTabs = ({ days, period, onChange }: PeriodTabsProps) => (
  <div className='flex items-center gap-4 px-3 py-2'>
    {PERIOD_OPTIONS.map((option) => (
      <SortButton
        key={option.value}
        onClick={() => onChange(option.value, null)}
        selected={option.value === days}
        noPulldown
      >
        {option.label}
      </SortButton>
    ))}
    <DisclosureSortDateBetweenButton
      defaultStartValue={period ? period.from?.toDateString() : undefined}
      defaultEndValue={period ? period.to?.toDateString() : undefined}
      onComplete={(min, max) => {
        if (!min && !max) {
          return
        }
        onChange(days, {
          from: min ? new Date(min) : undefined,
          to: max ? new Date(max) : undefined
        })
      }}
      quantity={[period?.from, period?.to].filter((d) => d).length}
    >
      {period
        ? `${period.from ? dateStr(period.from) : '----/--/--'} ~ ${period.to ? dateStr(period.to) : '----/--/--'}`
        : days
          ? `${dateStr(dayjs().subtract(days, 'day').toDate())} ~ ${dateStr(new Date())}`
          : '期間指定'}
    </DisclosureSortDateBetweenButton>
  </div>
)

// 設置場所（ページ）別の実績。ページ種別の一覧から、選んだ種別の URL 別へ降りる2階層で見せる。
// 商品詳細は商品ごとに別 URL になるため、URL 別だけでは「商品詳細に置いた枠の効果」が読めない

// ホスト名ではなく種別を表す値（backend の services/placement_url.py と対応）。
// この2つはパスが URL ではなく識別子そのもののため、URL 別へ降りる意味がない
const LABEL_ONLY_HOSTS = ['(mail)', '(placement)']

interface PlacementRowProps {
  placement: PlacementStat
  attribution: ConversionAttribution
  onDrill?: () => void
}

const PlacementRow = ({ placement, attribution, onDrill }: PlacementRowProps) => {
  const { conversions, revenue } = pickConversion(placement, attribution)
  const cvr =
    attribution === ConversionAttribution.DIRECT ? placement.direct_cvr : placement.assist_cvr

  return (
    <TableRow>
      {/* URL や識別子は長くなりうる。幅を決めて折り返さないと、1行が表を横へ押し広げ、
          右側の数値列が画面の外へ出てしまう（実機で 4540px まで広がった） */}
      <TableRowItem width={420}>
        {onDrill ? (
          <button type='button' className='text-left break-all underline' onClick={onDrill}>
            <Text size={14}>{placement.label}</Text>
          </button>
        ) : (
          <div className='break-all'>
            <Text size={14} color={placement.is_unmeasured ? TextColor.SECONDARY : undefined}>
              {placement.label}
            </Text>
          </div>
        )}
      </TableRowItem>
      <TableRowItem>
        <Text size={14}>{placement.impressions.toLocaleString()}</Text>
      </TableRowItem>
      <TableRowItem>
        <Text size={14}>{placement.clicks.toLocaleString()}</Text>
      </TableRowItem>
      <TableRowItem>
        <Badge badgeType={BadgeType.SECONDARY}>{formatPercent(placement.ctr)}</Badge>
      </TableRowItem>
      <TableRowItem>
        <Text size={14}>{conversions.toLocaleString()}</Text>
      </TableRowItem>
      <TableRowItem>
        <Badge badgeType={BadgeType.SECONDARY}>{formatPercent(cvr)}</Badge>
      </TableRowItem>
      <TableRowItem>
        <Text size={14}>{currencyText(revenue)}</Text>
      </TableRowItem>
    </TableRow>
  )
}

interface WidgetRowsProps {
  widget: WidgetDeliveryStat
  attribution: ConversionAttribution
}

const WidgetRows = ({ widget, attribution }: WidgetRowsProps) => {
  const sourceLabel =
    SOURCE_LABELS[widget.display_logic] ?? widgetDisplayLogicName(widget.display_logic) ?? '-'
  const isMail = widget.display_logic === 'mail'
  // 2026-08-28 修正: バックエンドの返却キーは ab_breakdown（配列）。
  // ab_groups を見ていたためA/Bパターンの内訳行が1件も表示されていなかった
  const abEntries = widget.ab_breakdown ?? []
  const { conversions, revenue } = pickConversion(widget, attribution)
  const cvr = attribution === ConversionAttribution.DIRECT ? widget.direct_cvr : widget.assist_cvr

  return (
    <>
      <TableRow>
        <TableRowItem>
          <Text size={14}>{widget.widget_id ?? '-'}</Text>
        </TableRowItem>
        <TableRowItem>
          <Text size={14}>{widget.widget_name}</Text>
        </TableRowItem>
        <TableRowItem>
          <Badge badgeType={isMail ? BadgeType.CAUTION : BadgeType.SQUARE_PRIMARY}>
            {sourceLabel}
          </Badge>
        </TableRowItem>
        <TableRowItem>
          <Text size={14} weight='bold'>
            {widget.impressions.toLocaleString()}
          </Text>
        </TableRowItem>
        <TableRowItem>
          <Text size={14} weight='bold'>
            {widget.clicks.toLocaleString()}
          </Text>
        </TableRowItem>
        <TableRowItem>
          <Badge badgeType={BadgeType.SECONDARY}>{formatPercent(widget.ctr)}</Badge>
        </TableRowItem>
        <TableRowItem>
          <Text size={14} weight='bold'>
            {conversions.toLocaleString()}
          </Text>
        </TableRowItem>
        <TableRowItem>
          <Badge badgeType={BadgeType.SUCCESS}>{formatPercent(cvr)}</Badge>
        </TableRowItem>
        <TableRowItem>
          <Text size={14}>{currencyText(Math.round(revenue))}</Text>
        </TableRowItem>
        <TableRowItem>
          <Text size={14}>{formatDwell(widget.avg_dwell_ms)}</Text>
        </TableRowItem>
        <TableRowItem>
          <Text size={14}>
            {widget.total_dwell_30s_rate != null
              ? widget.total_dwell_30s_rate.toFixed(2) + '%'
              : '-'}
          </Text>
        </TableRowItem>
      </TableRow>
      {abEntries.map((ab) => (
        <TableRow key={ab.group} attributes={{ className: 'bg-fill-surface-tertiary' }}>
          <TableRowItem />
          <TableRowItem>
            <div className='pl-6'>
              <Badge badgeType={BadgeType.SQUARE_TERTIARY}>パターン {ab.group}</Badge>
            </div>
          </TableRowItem>
          <TableRowItem />
          <TableRowItem>
            <Text size={12}>{ab.impressions.toLocaleString()}</Text>
          </TableRowItem>
          <TableRowItem>
            <Text size={12}>{ab.clicks.toLocaleString()}</Text>
          </TableRowItem>
          <TableRowItem>
            <Badge badgeType={BadgeType.SECONDARY}>
              {ab.impressions > 0 ? formatPercent(ab.clicks / ab.impressions) : '0.00%'}
            </Badge>
          </TableRowItem>
          <TableRowItem>
            <Text size={12}>{pickConversion(ab, attribution).conversions.toLocaleString()}</Text>
          </TableRowItem>
          <TableRowItem>
            <Badge badgeType={BadgeType.SUCCESS}>
              {ab.clicks > 0
                ? formatPercent(pickConversion(ab, attribution).conversions / ab.clicks)
                : '0.00%'}
            </Badge>
          </TableRowItem>
          <TableRowItem>
            <Text size={12}>
              {currencyText(Math.round(pickConversion(ab, attribution).revenue))}
            </Text>
          </TableRowItem>
          <TableRowItem />
        </TableRow>
      ))}
    </>
  )
}

const DeliveryStatsPage = () => {
  const siteId = useSiteId()
  // 2026-10-06 追加: メールレコメンド（enable_mail_reco）の ON/OFF で、メールの説明を出し分ける
  const { data: site } = useSiteGetApi({ site_id: siteId })

  const [summaryDate, setSummaryDate] = useState<Date | null>(null)
  // 表示するコンバージョン種別。直接とアシストは同じ受注を別の意味で数えた指標で、
  // 合算してはいけないため切り替えて見る
  const [attribution, setAttribution] = useState<ConversionAttribution>(
    DEFAULT_CONVERSION_ATTRIBUTION
  )
  // 設置場所別実績のドリルダウン先（null ならページ種別の一覧）
  const [drillTarget, setDrillTarget] = useState<{
    host: string
    pathPrefix: string
  } | null>(null)
  const formattedSummaryMonth = summaryDate
    ? `${summaryDate.getFullYear()}-${String(summaryDate.getMonth() + 1).padStart(2, '0')}`
    : null

  const {
    data: deliveryStats,
    isLoading,
    error
  } = useDeliveryStatsGetApi({
    site_id: siteId,
    ...(formattedSummaryMonth && { month: formattedSummaryMonth })
  })
  const { data: dashboard } = useSiteDashboardGetApi({
    site_id: siteId,
    ...(formattedSummaryMonth && { month: formattedSummaryMonth })
  })

  // 複数のレコメンドパーツへまたがって計上された注文の件数。
  // パーツ別の数字を足し上げてもサマリーにならない理由を、実数として注記に出す
  const multiWidgetOrders =
    (attribution === ConversionAttribution.DIRECT
      ? deliveryStats?.multi_widget_orders_direct
      : deliveryStats?.multi_widget_orders_assist) ?? 0
  const { data: recommendationPerformance } = useAnalyticsRecommendationPerformanceGetApi({
    site_id: siteId,
    ...(formattedSummaryMonth && { month: formattedSummaryMonth })
  })

  // 設置場所別の実績。ページ種別の一覧と、選んだ種別の URL 別を同じ枠で切り替えて見せる
  const { data: placementStats } = usePlacementStatsGetApi({
    site_id: siteId,
    ...(formattedSummaryMonth && { month: formattedSummaryMonth }),
    group: drillTarget ? 'url' : 'type',
    ...(drillTarget && { host: drillTarget.host, path_prefix: drillTarget.pathPrefix })
  })

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='配信結果/分析'
        badgeNode={<AppTitleBadge type='recommend' />}
        description='レコメンドパーツ別の表示回数・クリック数・コンバージョン数'
      />
      <MainInnerLoader error={error} data={deliveryStats} withSide>
        {deliveryStats && !isLoading && (
          <Content>
            {/* レコメンド成果 + 行動・受注推移（ダッシュボードと同じ表示） */}
            {dashboard && recommendationPerformance && (
              <>
                <div>
                  <SummaryHeading>
                    <ItemAlign justify='between'>
                      <ItemAlign align='center'>
                        レコメンド成果
                        <TextPager
                          value={dayjs(summaryDate ?? new Date()).format('YYYY年M月')}
                          onClickPrev={() => {
                            setSummaryDate((d) =>
                              dayjs(d ?? new Date())
                                .subtract(1, 'month')
                                .toDate()
                            )
                          }}
                          onClickNext={
                            summaryDate &&
                            dayjs().startOf('month').isAfter(dayjs(summaryDate).startOf('month'))
                              ? () => {
                                  setSummaryDate((d) =>
                                    dayjs(d ?? new Date())
                                      .add(1, 'month')
                                      .toDate()
                                  )
                                }
                              : undefined
                          }
                        />
                      </ItemAlign>
                      <ConversionAttributionToggle value={attribution} onSelect={setAttribution} />
                    </ItemAlign>
                  </SummaryHeading>
                  <Area noBorder>
                    <RecommendResult
                      dashboard={dashboard}
                      recommendationPerformance={recommendationPerformance}
                      attribution={attribution}
                    />
                  </Area>
                </div>
                <ActionTransition
                  dashboard={dashboard}
                  recommendationPerformance={recommendationPerformance}
                />
              </>
            )}
            <Area>
              <ChartWrapper>
                <ChartTitle>日別推移</ChartTitle>
                <BarChart
                  labels={deliveryStats.daily.map((stat) => dayjs(stat.date).format('M/D'))}
                  items={[
                    {
                      title: 'PV数',
                      values: deliveryStats.daily.map((stat) => stat.impressions)
                    },
                    {
                      title: 'クリック数',
                      values: deliveryStats.daily.map((stat) => stat.clicks)
                    },
                    {
                      title: 'CV数',
                      values: deliveryStats.daily.map((stat) =>
                        attribution === ConversionAttribution.DIRECT
                          ? stat.direct_conversions
                          : stat.assist_conversions
                      )
                    }
                  ]}
                />
              </ChartWrapper>
            </Area>
            {/* ウィジェット別実績 */}
            <Area noBorder>
              <ChartWrapper>
                <ChartTitle>日別推移</ChartTitle>
                <BarAndLineChart
                  labels={deliveryStats.daily.map((stat) => dayjs(stat.date).format('M/D'))}
                  barItems={[
                    {
                      title: 'サイト閲覧数',
                      values: deliveryStats.daily.map((stat) => stat.impressions)
                    }
                  ]}
                  lineItems={[
                    {
                      title: 'クリック率',
                      values: deliveryStats.daily.map((stat) =>
                        percentPoint(stat.clicks, stat.impressions)
                      )
                    },
                    {
                      // 分母はクリック数。パーツ別・設置場所別テーブルの CVR
                      // （backend の direct_cvr / assist_cvr = CV ÷ クリック数）と定義を揃える。
                      // 表示回数で割ると、同じ「CVR」のラベルで1桁違う値が同じ画面に並ぶ
                      title: 'CVR',
                      values: deliveryStats.daily.map((stat) => {
                        const cv =
                          attribution === ConversionAttribution.DIRECT
                            ? stat.direct_conversions
                            : stat.assist_conversions
                        return percentPoint(cv, stat.clicks)
                      })
                    }
                  ]}
                />
              </ChartWrapper>
              {deliveryStats.widgets.length === 0 ? (
                <div className='py-10 text-center'>
                  <Text color={TextColor.SECONDARY}>
                    配信データがありません。ウィジェットを設置してレコメンドを配信すると、ここにデータが表示されます。
                  </Text>
                </div>
              ) : (
                <>
                  <Table>
                    <thead>
                      <TableRow head>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            ID
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            レコメンドパーツ
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            表示元
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            表示回数
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            クリック数
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            CTR
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            CV数
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            CVR
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            売上
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            滞在時間
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            滞在率
                          </Text>
                        </TableHeadItem>
                      </TableRow>
                    </thead>
                    <tbody>
                      {deliveryStats.widgets.map((widget, i) => (
                        <WidgetRows key={i} widget={widget} attribution={attribution} />
                      ))}
                    </tbody>
                  </Table>
                  <div className='bg-fill-surface-tertiary mt-3 rounded-md px-4 py-3'>
                    <Text size={12} color={TextColor.SECONDARY}>
                      1つの注文で複数の商品を購入し、それぞれ別のレコメンドパーツ経由だった場合は、各パーツに
                      CV数・受注件数・注文金額がそれぞれ計上されます。そのため、パーツ別の数字を合計してもサマリーとは一致しません。
                      {multiWidgetOrders > 0
                        ? `該当する注文は ${multiWidgetOrders.toLocaleString()}件 ありました。`
                        : '該当する注文はありませんでした。'}
                    </Text>
                  </div>
                </>
              )}
            </Area>
            {/* 設置場所（ページ）別実績 */}
            <Area noBorder>
              <SummaryHeading>
                <ItemAlign justify='between'>
                  <ItemAlign align='center'>
                    設置場所別の実績
                    {drillTarget && (
                      <button
                        type='button'
                        className='ml-3 underline'
                        onClick={() => setDrillTarget(null)}
                      >
                        <Text size={12} color={TextColor.SECONDARY}>
                          ページ種別の一覧へ戻る
                        </Text>
                      </button>
                    )}
                  </ItemAlign>
                  <ConversionAttributionToggle value={attribution} onSelect={setAttribution} />
                </ItemAlign>
              </SummaryHeading>
              {!placementStats || placementStats.placements.length === 0 ? (
                <div className='py-10 text-center'>
                  <Text color={TextColor.SECONDARY}>
                    設置場所のデータがありません。計測タグを更新すると、レコメンドを表示したページごとの実績がここに表示されます。
                  </Text>
                </div>
              ) : (
                <>
                  <Table>
                    <thead>
                      <TableRow head>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            {drillTarget ? 'ページURL' : '設置場所'}
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            表示回数
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            クリック数
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            CTR
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            CV数
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            CVR
                          </Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text color={TextColor.SECONDARY} size={12}>
                            売上
                          </Text>
                        </TableHeadItem>
                      </TableRow>
                    </thead>
                    <tbody>
                      {placementStats.placements.map((placement, i) => (
                        <PlacementRow
                          key={i}
                          placement={placement}
                          attribution={attribution}
                          // 計測前の行と、URL 別の一覧では、これ以上降りられない
                          onDrill={
                            // これ以上降りられない行は、リンクにしない：
                            // ・URL 別の一覧（すでに最下層）
                            // ・計測前（設置場所を持たない）
                            // ・ホストを持たない行（絞り込みキーにできない）
                            // ・メールと識別子指定（種別とURLが同じで、降りても同じ1行しか出ない）
                            drillTarget ||
                            placement.is_unmeasured ||
                            !placement.placement_path ||
                            !placement.placement_host ||
                            LABEL_ONLY_HOSTS.includes(placement.placement_host)
                              ? undefined
                              : () =>
                                  setDrillTarget({
                                    host: placement.placement_host as string,
                                    pathPrefix: placement.placement_path as string
                                  })
                          }
                        />
                      ))}
                    </tbody>
                  </Table>
                  <div className='bg-fill-surface-tertiary mt-3 rounded-md px-4 py-3'>
                    {/* 文字列リテラルで書く。日本語の文をそのまま改行すると、JSX が改行を
                      半角スペースに変えて「埋め込んだ タグ」のように不自然な空きができる */}
                    <Text size={12} color={TextColor.SECONDARY}>
                      {'設置場所は、レコメンドを表示したページの URL で数えます。' +
                        // 2026-10-06 修正: メールの説明はメールレコメンドが ON のサイトだけに出す
                        (site?.enable_mail_reco
                          ? 'メール配信は埋め込んだタグの識別子で表します' +
                            '（識別子を書いていないメールは「メール: (未設定)」にまとまります）。'
                          : '') +
                        '設置場所の記録を始める前の実績は「(計測前)」の行にまとまり、' +
                        '他の設置場所には振り分けられません。'}
                    </Text>
                  </div>
                </>
              )}
            </Area>
          </Content>
        )}
      </MainInnerLoader>
    </Main>
  )
}

export default DeliveryStatsPage
